/**
 * Provider acquisition popup UI (visitor campaigns).
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { TransitionProps } from '@mui/material/transitions';
import type {
  ProviderAcquisitionCampaign,
  ProviderAcquisitionContent,
} from '../../hooks/useProviderAcquisitionCampaigns';

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface ProviderAcquisitionPopupProps {
  campaign: ProviderAcquisitionCampaign;
  content: ProviderAcquisitionContent;
  onDismiss: (action?: 'dismissed' | 'closed') => void;
  onCtaClick: (ctaType: 'primary' | 'secondary', url?: string) => void;
  onShow?: () => void;
}

export const ProviderAcquisitionPopup: React.FC<ProviderAcquisitionPopupProps> = ({
  campaign,
  content,
  onDismiss,
  onCtaClick,
  onShow,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
      onShow?.();
    }, 100);
    return () => clearTimeout(timer);
  }, [onShow]);

  useEffect(() => {
    if (campaign.popup_type === 'banner' && visible) {
      const timer = setTimeout(() => handleDismiss('dismissed'), 8000);
      return () => clearTimeout(timer);
    }
  }, [campaign.popup_type, visible]);

  const handleDismiss = (action: 'dismissed' | 'closed' = 'dismissed') => {
    setVisible(false);
    setTimeout(() => onDismiss(action), 300);
  };

  const customStyles = {
    backgroundColor: content.background_color || theme.palette.primary.main,
    color: content.text_color || theme.palette.primary.contrastText,
  };

  const body = (
    <Box sx={{ ...customStyles, p: 3, position: 'relative' }}>
      <IconButton
        aria-label="Close"
        onClick={() => handleDismiss('closed')}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'inherit' }}
        size="small"
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" component="h2" fontWeight={700} gutterBottom pr={4}>
        {content.title}
      </Typography>
      {content.subtitle && (
        <Typography variant="subtitle1" sx={{ opacity: 0.95, mb: 1 }}>
          {content.subtitle}
        </Typography>
      )}
      {content.body_text && (
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
          {content.body_text}
        </Typography>
      )}
      {content.bullets && content.bullets.length > 0 && (
        <Box component="ul" sx={{ pl: 2.5, mb: 2, opacity: 0.95 }}>
          {content.bullets.map((b) => (
            <Typography key={b} component="li" variant="body2" sx={{ mb: 0.5 }}>
              {b}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
        <Button
          variant="contained"
          onClick={() => onCtaClick('primary', content.primary_cta_url)}
          sx={{
            bgcolor: 'background.paper',
            color: customStyles.backgroundColor,
            fontWeight: 700,
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          {content.primary_cta_text}
        </Button>
        {content.secondary_cta_text && content.secondary_cta_url && (
          <Button
            variant="outlined"
            onClick={() => onCtaClick('secondary', content.secondary_cta_url)}
            sx={{ borderColor: 'currentColor', color: 'inherit' }}
          >
            {content.secondary_cta_text}
          </Button>
        )}
      </Box>
    </Box>
  );

  if (campaign.popup_type === 'banner') {
    return (
      <Slide in={visible} direction="down">
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.modal + 1,
            borderRadius: 0,
          }}
        >
          {body}
        </Paper>
      </Slide>
    );
  }

  if (campaign.popup_type === 'slide_in') {
    return (
      <Slide in={visible} direction="up">
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 24,
            right: isMobile ? 0 : 24,
            left: isMobile ? 0 : 'auto',
            maxWidth: isMobile ? '100%' : 420,
            zIndex: theme.zIndex.modal + 1,
            borderRadius: isMobile ? 0 : 2,
          }}
        >
          {body}
        </Paper>
      </Slide>
    );
  }

  return (
    <Dialog
      open={visible}
      onClose={() => handleDismiss('closed')}
      maxWidth="sm"
      fullWidth
      fullScreen={campaign.popup_type === 'fullscreen'}
      TransitionComponent={SlideTransition}
    >
      <DialogContent sx={{ p: 0 }}>{body}</DialogContent>
    </Dialog>
  );
};

export default ProviderAcquisitionPopup;
